import { IDatabaseLineItem } from "../../pages/CreatorTools/LineItemDBCreationTool/types";
import { Rule, TransformationQuantityFunction, TransformationRuleInputs, TransformationRuleOverrides } from "@rebuild/shared";
import { getAllValidPermutations } from "../getAllValidPermutations";
import { normalizeString } from "../utils";
import { LineItemTransformationRuleInputPaths, resolveArrayPath, ValidationError, ValidationErrorEntry } from "./types";

type LineItemTransformationRule = Rule & { templateId: "line_item_transformation" };
type ValidationResult = { path: string; validationError: ValidationError; isOverride?: boolean };

const VALID_QUANTITY_CONVERSION_TYPES: TransformationQuantityFunction[] = ["MAX_VALUE", "MULTIPLY", "USE_DEFAULT"];

// --- Initial line item checks ---

function checkInitialLineItemMissing(rule: LineItemTransformationRule): ValidationResult | undefined {
  if (!rule.ruleInputs.initialLineItem?.lineItemId) {
    return { path: "initialLineItem", validationError: ValidationError.InitialLineItemNotSelected };
  }
}

function checkInitialLineItemInvalid(rule: LineItemTransformationRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
  const id = rule.ruleInputs.initialLineItem?.lineItemId;
  if (id && !dbLineItems.some((li) => li.id === id)) {
    return { path: "initialLineItem", validationError: ValidationError.InvalidInitialLineItem };
  }
}

// --- Final line item checks ---

function checkFinalLineItemInvalid(rule: LineItemTransformationRule, dbLineItems: IDatabaseLineItem[]): ValidationResult | undefined {
  const id = rule.ruleInputs.finalLineItem?.lineItemId;
  if (id && !dbLineItems.some((li) => li.id === id)) {
    return { path: "finalLineItem", validationError: ValidationError.InvalidFinalLineItem };
  }
}

// We only require the "final permutation" field if the final line item is defined and DOES NOT match the initial line item
function checkFinalLineItemPermutationMissing(rule: LineItemTransformationRule): ValidationResult | undefined {
  const finalId = rule.ruleInputs.finalLineItem?.lineItemId;
  const initialId = rule.ruleInputs.initialLineItem?.lineItemId;
  if (!finalId || !initialId || finalId === initialId) return undefined;
  if (!rule.ruleInputs.finalLineItem?.selectedPermutation) {
    return { path: "finalLineItem", validationError: ValidationError.MissingFinalLineItemPermutation };
  }
}

function checkFinalLineItemPermutationInvalid(
  rule: LineItemTransformationRule,
  dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
  const { lineItemId: id, selectedPermutation: permutation } = rule.ruleInputs.finalLineItem ?? {};
  if (!id || !permutation) return undefined;
  const dbLineItem = dbLineItems.find((li) => li.id === id);
  if (!dbLineItem) return undefined; // already caught by checkFinalLineItemInvalid
  if (!getAllValidPermutations(dbLineItem.elements).includes(normalizeString(permutation))) {
    return { path: "finalLineItem", validationError: ValidationError.InvalidFinalLineItemPermutation };
  }
}

// --- Quantity conversion checks ---

function checkQuantityConversionValueMissing(rule: LineItemTransformationRule): ValidationResult | undefined {
  const { quantityConversionType, quantityConversionValue } = rule.ruleInputs;
  if (quantityConversionType !== undefined && quantityConversionValue === undefined) {
    return { path: "quantityConversionValue", validationError: ValidationError.MissingQuantityConversionValue };
  }
}

function checkQuantityConversionTypeInvalid(rule: LineItemTransformationRule): ValidationResult | undefined {
  const { quantityConversionType } = rule.ruleInputs;
  if (quantityConversionType !== undefined && !VALID_QUANTITY_CONVERSION_TYPES.includes(quantityConversionType)) {
    return { path: "quantityConversionType", validationError: ValidationError.InvalidQuantityConversionType };
  }
}

function checkOverridePermutationLineItemInvalid(
  rule: LineItemTransformationRule,
  override: TransformationRuleOverrides,
  index: number,
): ValidationResult | undefined {
  const overrideLineItemId = override.permutation?.lineItemId;
  const initialLineItemId = rule.ruleInputs.initialLineItem?.lineItemId;
  if (!overrideLineItemId || !initialLineItemId) return undefined;
  if (overrideLineItemId !== initialLineItemId) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].permutation", index),
      validationError: ValidationError.InvalidOverridePermutationLineItem,
      isOverride: true,
    };
  }
}

function checkOverrideFinalLineItemInvalid(
  override: TransformationRuleOverrides,
  index: number,
  dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
  const id = override.finalLineItem?.lineItemId;
  if (id && !dbLineItems.some((li) => li.id === id)) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].finalLineItem", index),
      validationError: ValidationError.InvalidFinalLineItem,
      isOverride: true,
    };
  }
}

function checkOverrideFinalLineItemPermutationMissing(
  rule: LineItemTransformationRule,
  override: TransformationRuleOverrides,
  index: number,
): ValidationResult | undefined {
  const finalId = override.finalLineItem?.lineItemId;
  const initialId = rule.ruleInputs.initialLineItem?.lineItemId;
  if (!finalId || !initialId || finalId === initialId) return undefined;
  if (!override.finalLineItem?.selectedPermutation) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].finalLineItem", index),
      validationError: ValidationError.MissingFinalLineItemPermutation,
      isOverride: true,
    };
  }
}

function checkOverrideFinalLineItemPermutationInvalid(
  rule: LineItemTransformationRule,
  override: TransformationRuleOverrides,
  index: number,
  dbLineItems: IDatabaseLineItem[],
): ValidationResult | undefined {
  const { lineItemId: id, selectedPermutation: permutation } = override.finalLineItem ?? {};
  if (!id || !permutation) return undefined;
  const dbLineItem = dbLineItems.find((li) => li.id === id);

  // already caught by checkOverrideFinalLineItemInvalid
  if (!dbLineItem) return undefined;

  // We only require the "final permutation" field if the final line item is defined and DOES NOT match the initial line item
  if (
    rule.ruleInputs.initialLineItem.lineItemId === undefined ||
    rule.ruleInputs.initialLineItem.lineItemId === override.finalLineItem.lineItemId
  ) {
    return undefined;
  }

  if (!getAllValidPermutations(dbLineItem.elements).includes(normalizeString(permutation))) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].finalLineItem", index),
      validationError: ValidationError.InvalidFinalLineItemPermutation,
      isOverride: true,
    };
  }
}

function checkOverrideQuantityConversionValueMissing(override: TransformationRuleOverrides, index: number): ValidationResult | undefined {
  if (override.quantityConversionType !== undefined && override.quantityConversionValue === undefined) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].quantityConversionValue", index),
      validationError: ValidationError.MissingQuantityConversionValue,
      isOverride: true,
    };
  }
}

function checkOverrideQuantityConversionTypeInvalid(override: TransformationRuleOverrides, index: number): ValidationResult | undefined {
  if (override.quantityConversionType !== undefined && !VALID_QUANTITY_CONVERSION_TYPES.includes(override.quantityConversionType)) {
    return {
      path: resolveArrayPath<TransformationRuleInputs>("overrides[number].quantityConversionType", index),
      validationError: ValidationError.InvalidQuantityConversionType,
      isOverride: true,
    };
  }
}

export function validateLineItemTransformation(
  rule: LineItemTransformationRule,
  dbLineItems: IDatabaseLineItem[],
): Map<LineItemTransformationRuleInputPaths, ValidationErrorEntry> | undefined {
  const results: ValidationResult[] = [
    checkInitialLineItemMissing(rule),
    checkInitialLineItemInvalid(rule, dbLineItems),
    checkFinalLineItemInvalid(rule, dbLineItems),
    checkFinalLineItemPermutationMissing(rule),
    checkFinalLineItemPermutationInvalid(rule, dbLineItems),
    checkQuantityConversionValueMissing(rule),
    checkQuantityConversionTypeInvalid(rule),
    ...(rule.ruleInputs.overrides ?? []).flatMap((override, i) => [
      checkOverridePermutationLineItemInvalid(rule, override, i),
      checkOverrideFinalLineItemInvalid(override, i, dbLineItems),
      checkOverrideFinalLineItemPermutationMissing(rule, override, i),
      checkOverrideFinalLineItemPermutationInvalid(rule, override, i, dbLineItems),
      checkOverrideQuantityConversionValueMissing(override, i),
      checkOverrideQuantityConversionTypeInvalid(override, i),
    ]),
  ].filter((r): r is ValidationResult => r !== undefined);

  if (results.length === 0) return undefined;

  return new Map(
    results.map(({ path, validationError, isOverride }) => [
      path as LineItemTransformationRuleInputPaths,
      { validationError, ...(isOverride ? { isInOverride: true } : {}) } satisfies ValidationErrorEntry,
    ]),
  );
}
